import { Character } from '../types/character';
import { Universe, universeCharacteristics, universeLabels } from '../types/template';

export function convertGDocToCharacter(text: string): Character {
  // Normalize line breaks and spaces
  text = text.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  
  // Split text into sections based on different heading formats
  const sections = text.split(/(?=(?:^|\n)(?:#+\s|[A-Z][^:\n]*:|[A-Z][^:\n]*\n[-=]+))/m)
    .filter(Boolean)
    .map(s => s.trim());
  
  // Extract main title and universe
  const titlePatterns = [
    /^#\s+(.+?)(?:\s*\((.+?)\))?(?:\s*$|\n)/m,
    /^(.+?)(?:\s*\((.+?)\))?(?:\s*$|\n=+)/m,
    /^(.+?)(?:\s*\[(.+?)\])?(?:\s*$|\n)/m,
    /^Nom\s*:?\s*(.+?)(?:\s*\((.+?)\))?(?:\s*$|\n)/mi
  ];

  let name = 'Nouveau Personnage';
  let universeHint = '';

  for (const pattern of titlePatterns) {
    const match = sections[0]?.match(pattern);
    if (match) {
      name = match[1].trim();
      universeHint = match[2]?.trim() || '';
      break;
    }
  }

  // Determine universe
  const universe = determineUniverse(universeHint || text);

  // Parse sections
  const parsedSections = sections.slice(1).map(section => {
    if (section.startsWith('#')) {
      return parseMarkdownSection(section);
    }
    
    if (section.match(/\n[-=]+\n/)) {
      return parseUnderlinedSection(section);
    }
    
    const colonMatch = section.match(/^([A-Z][^:\n]*):(.+)/s);
    if (colonMatch) {
      return {
        title: colonMatch[1].trim(),
        content: colonMatch[2].trim(),
        level: 2
      };
    }

    const simpleMatch = section.match(/^([A-Z][^\n]+)\n\n(.+)/s);
    if (simpleMatch) {
      return {
        title: simpleMatch[1].trim(),
        content: simpleMatch[2].trim(),
        level: 2
      };
    }

    return null;
  }).filter(Boolean);

  // Create character sections
  const mainSections = [
    {
      id: '1',
      title: 'Description Physique',
      content: extractPhysicalDescription(text) || ''
    },
    {
      id: '2',
      title: 'Personnalité',
      content: extractPersonalityDescription(text) || ''
    }
  ];

  // Add universe characteristics
  const characteristicSections = universeCharacteristics[universe].map((title, index) => ({
    id: `char-${index + 3}`,
    title,
    content: findCharacteristicContent(text, title)
  }));

  // Create character
  const character: Character = {
    id: Date.now().toString(),
    name,
    universe,
    themeColor: determineThemeColor(text),
    profileImage: extractProfileImage(text) || '',
    backgroundImage: extractBackgroundImage(text) || '',
    sections: [...mainSections, ...characteristicSections],
    customSections: [],
    illustrations: extractIllustrations(text),
    lastModified: Date.now()
  };

  return character;
}

function parseMarkdownSection(text: string) {
  const lines = text.trim().split('\n');
  const titleMatch = lines[0].match(/^(#+)\s+(.+)$/);
  
  if (!titleMatch) return null;
  
  return {
    title: titleMatch[2].trim(),
    content: lines.slice(1).join('\n').trim(),
    level: titleMatch[1].length
  };
}

function parseUnderlinedSection(text: string) {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return null;

  const underlineMatch = lines[1].match(/^[-=]+$/);
  if (!underlineMatch) return null;

  return {
    title: lines[0].trim(),
    content: lines.slice(2).join('\n').trim(),
    level: lines[1][0] === '=' ? 1 : 2
  };
}

function determineUniverse(text: string): Universe {
  const keywords = {
    'hogwarts-legacy': ['hogwarts', 'poudlard', 'magie', 'sorcier', 'baguette'],
    'space-marine': ['space marine', 'warhammer', 'imperium', 'astartes'],
    'medieval': ['medieval', 'médiéval', 'chevalier', 'château'],
    'fantastic': ['fantasy', 'fantastique', 'elfe', 'nain', 'orc']
  };

  let maxScore = 0;
  let bestMatch: Universe = 'medieval';

  for (const [universe, words] of Object.entries(keywords)) {
    const score = words.reduce((acc, word) => {
      const regex = new RegExp(word, 'gi');
      const matches = text.match(regex);
      return acc + (matches ? matches.length : 0);
    }, 0);

    if (score > maxScore) {
      maxScore = score;
      bestMatch = universe as Universe;
    }
  }

  return bestMatch;
}

function findCharacteristicContent(text: string, title: string): string {
  const patterns = [
    new RegExp(`${title}\\s*:?\\s*([^.\\n]+)`, 'i'),
    new RegExp(`${title.toLowerCase()}\\s*:?\\s*([^.\\n]+)`, 'i')
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match[1].trim();
  }

  return '';
}

function extractPhysicalDescription(text: string): string {
  const patterns = [
    /(?:il|elle)\s+(?:est|mesure|porte|a)\s+[^.]+\./gi,
    /(?:ses|ces)\s+(?:yeux|cheveux|traits)\s+[^.]+\./gi,
    /physiquement[^.]+\./gi,
    /apparence[^.]+\./gi
  ];

  return extractMatchingContent(text, patterns);
}

function extractPersonalityDescription(text: string): string {
  const patterns = [
    /(?:il|elle)\s+(?:aime|déteste|préfère)\s+[^.]+\./gi,
    /son\s+(?:caractère|tempérament)\s+[^.]+\./gi,
    /personnalité[^.]+\./gi,
    /attitude[^.]+\./gi
  ];

  return extractMatchingContent(text, patterns);
}

function extractMatchingContent(text: string, patterns: RegExp[]): string {
  const matches = patterns
    .flatMap(pattern => text.match(pattern) || [])
    .filter(Boolean);

  return matches.join(' ');
}

function determineThemeColor(text: string): ThemeColor {
  const colorKeywords = {
    'purple': ['violet', 'purple', 'mauve'],
    'blue': ['blue', 'bleu', 'azure'],
    'green': ['green', 'vert', 'emerald'],
    'amber': ['amber', 'ambre', 'gold'],
    'rose': ['rose', 'pink', 'coral'],
    'teal': ['teal', 'turquoise', 'cyan'],
    'orange': ['orange', 'sunset', 'flame'],
    'red': ['red', 'rouge', 'crimson'],
    'indigo': ['indigo', 'deep blue', 'navy'],
    'emerald': ['emerald', 'émeraude', 'jade']
  };

  let maxScore = 0;
  let bestMatch: ThemeColor = 'purple';

  for (const [color, keywords] of Object.entries(colorKeywords)) {
    const score = keywords.reduce((acc, word) => {
      const regex = new RegExp(word, 'gi');
      const matches = text.match(regex);
      return acc + (matches ? matches.length : 0);
    }, 0);

    if (score > maxScore) {
      maxScore = score;
      bestMatch = color as ThemeColor;
    }
  }

  return bestMatch;
}

function extractProfileImage(text: string): string {
  const patterns = [
    /(?:profile|avatar|portrait)\s*:?\s*(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/i,
    /(?:photo|image)\s+(?:de\s+)?profil\s*:?\s*(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/i
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match[1];
  }

  return '';
}

function extractBackgroundImage(text: string): string {
  const patterns = [
    /(?:background|banner|fond)\s*:?\s*(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/i,
    /(?:image|photo)\s+(?:de\s+)?(?:fond|couverture)\s*:?\s*(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/i
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) return match[1];
  }

  return '';
}

function extractIllustrations(text: string): { id: string; url: string; caption: string; }[] {
  const illustrations = [];
  const urlPattern = /(https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp))/gi;
  const captionPattern = /\(([^)]+)\)/g;

  let match;
  let index = 0;
  while ((match = urlPattern.exec(text)) !== null) {
    illustrations.push({
      id: `ill-${Date.now()}-${index}`,
      url: match[1],
      caption: ''
    });
    index++;
  }

  // Try to match captions with illustrations
  let captionMatch;
  index = 0;
  while ((captionMatch = captionPattern.exec(text)) !== null && index < illustrations.length) {
    illustrations[index].caption = captionMatch[1].trim();
    index++;
  }

  return illustrations;
}